import { useContext, useRef, useState } from 'react'
import { IconButton, Menu, MenuItem } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import SequenceMoveContext from './SequenceMoveContext'

const stopRowClick = (event) => event.stopPropagation()
const DOES_NOTHING = () => {}

/**
 * The "..." menu of a row in a machine's tab (CU7): move it to a position (opens the position field), to the top
 * or to the bottom (the same request the field and the arrows make for that target), or see its detail (what the
 * (i) button does). The three ways to move are disabled without "PLANNER_DETAIL_ORDER", while a move is being
 * saved, and where they would move nothing; "View detail" is always available. Neither the button nor the options
 * reach the row (RN47).
 *
 * @param {Object} props
 * @param {Object} props.row The list row, with Sun's `position`
 * @param {Function} props.onViewDetail Called with the row
 */
const SequenceRowMenu = ({ row, onViewDetail }) => {
  const { t_ } = useSmartTranslation()
  const move = useContext(SequenceMoveContext)
  const buttonRef = useRef()
  const [open, setOpen] = useState(false)

  if (!move.enabled) return null

  const hasPosition = Number.isInteger(row.position)
  const cannotMove = !move.canOrder || move.saving || !hasPosition
  const atTop = hasPosition && row.position <= 1
  const atBottom = hasPosition && row.position >= move.jobCount
  const close = () => setOpen(false)
  // A disabled option does nothing, whatever reaches it (CSS keeps the pointer off it, not the event).
  const choose = (action, isDisabled) => {
    if (isDisabled) return DOES_NOTHING
    return () => {
      close()
      action()
    }
  }
  // These options open (or replace) the position field in the same click: the open one must not close first (S205).
  const keepOpen = { 'data-sequence-keep-open': 'true' }

  return (
    <span onClick={stopRowClick}>
      <IconButton ref={buttonRef} aria-label={t_('More options')} aria-haspopup='menu' onClick={() => setOpen(true)} {...keepOpen}>
        <MoreVertIcon />
      </IconButton>
      <Menu open={open} anchorEl={buttonRef.current} onClose={close} PaperProps={keepOpen}>
        <MenuItem {...keepOpen} disabled={cannotMove} onClick={choose(() => move.openField(row), cannotMove)}>{t_('Move to position...')}</MenuItem>
        <MenuItem {...keepOpen} disabled={cannotMove || atTop} onClick={choose(() => move.moveTo(row, 1), cannotMove || atTop)}>{t_('Move to the top')}</MenuItem>
        <MenuItem {...keepOpen} disabled={cannotMove || atBottom} onClick={choose(() => move.moveTo(row, move.jobCount), cannotMove || atBottom)}>{t_('Move to the bottom')}</MenuItem>
        <MenuItem {...keepOpen} onClick={choose(() => onViewDetail(row), false)}>{t_('View detail')}</MenuItem>
      </Menu>
    </span>
  )
}

export default SequenceRowMenu
